import { create } from 'zustand';
import { getTenantId } from './authStore';
import i18n from '../i18n';

const AGENT_API = import.meta.env.VITE_AGENT_SERVICE_URL || 'http://localhost:8013';

export interface KnowledgeScopeEntry {
  object_type_id: string;
  label: string;
  filter?: { field: string; op: string; value: string } | null;
}

export interface AgentEventRule {
  name: string;
  description: string;
  properties: string[];
}

export interface AgentConfig {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  system_prompt: string;
  model: string;
  enabled_tools: string[];
  tool_config: Record<string, unknown>;
  max_iterations: number;
  knowledge_scope: KnowledgeScopeEntry[] | null; // null = unrestricted
  event_rules?: AgentEventRule[];
  enabled: boolean;
  // When on, inbound images (e.g. WhatsApp) are sent to the multimodal model
  // as vision context for that turn (customer sends a photo + "¿tienen esto?").
  image_understanding_enabled?: boolean;
  // Object Studio destinations for the Chats app. Null = no mirroring.
  conversation_ot_id?: string | null;
  message_ot_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface AgentThread {
  id: string;
  agent_id: string;
  title?: string;
  status: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AgentMessage {
  id: string;
  thread_id: string;
  role: string;
  content: string;
  tool_name?: string;
  tool_use_id?: string;
  tool_input?: Record<string, unknown>;
  tool_result?: unknown;
  created_at?: string;
}

export interface StreamingTool {
  name: string;
  status: 'calling' | 'done' | 'error';
  input?: Record<string, unknown>;
  result?: unknown;
}

export interface AgentVersion {
  id: string;
  agent_id: string;
  version_number: number;
  config_snapshot: AgentConfig;
  created_at: string;
}

export interface AgentSchedule {
  id: string;
  agent_id: string;
  tenant_id: string;
  name: string;
  prompt: string;
  cron_expression: string;
  enabled: boolean;
  last_run_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface FilterClause {
  field: string;
  op: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'not_in'
    | 'contains' | 'starts_with' | 'ends_with' | 'is_null' | 'is_not_null';
  value?: unknown;
}

export interface PipelineTrigger {
  id: string;
  tenant_id: string;
  name: string;
  pipeline_id: string;
  agent_id: string;
  on_new_only: boolean;
  min_new_rows: number;
  mode: 'per_row' | 'per_batch';
  max_concurrent: number;
  prompt_template: string;
  row_filter: FilterClause[];
  dedupe_action_name?: string | null;
  dedupe_field?: string | null;
  enabled: boolean;
  last_fired_at?: string | null;
  last_fire_summary?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
}

export type PipelineTriggerCreate = Omit<
  PipelineTrigger,
  'id' | 'tenant_id' | 'last_fired_at' | 'last_fire_summary' | 'created_at' | 'updated_at'
>;

/** A chat turn that ended without an answer. `message` is the user-safe text
 *  from the stream's terminal `error` event (backend shared/provider_errors.py);
 *  `code` is its machine code, or `network` / `http_error` / `empty_stream`
 *  when the failure happened client-side. `content` is the user message to
 *  resend on retry. */
export interface ChatTurnError {
  threadId: string;
  content: string;
  code: string;
  message: string;
  retryable: boolean;
}

interface AgentStore {
  agents: AgentConfig[];
  selectedAgent: AgentConfig | null;
  threads: AgentThread[];
  selectedThread: AgentThread | null;
  messages: AgentMessage[];
  availableTools: string[];
  loading: boolean;
  sending: boolean;
  streamingText: string;
  streamingTools: StreamingTool[];
  chatError: ChatTurnError | null;
  schedules: AgentSchedule[];
  triggers: PipelineTrigger[];

  fetchAgents: () => Promise<void>;
  selectAgent: (agent: AgentConfig | null) => void;
  createAgent: (data: Partial<AgentConfig>) => Promise<AgentConfig>;
  updateAgent: (id: string, data: Partial<AgentConfig>) => Promise<void>;
  deleteAgent: (id: string) => Promise<void>;
  setKnowledgeScope: (id: string, scope: KnowledgeScopeEntry[] | null) => Promise<void>;
  fetchAvailableTools: () => Promise<void>;

  fetchThreads: (agentId: string) => Promise<void>;
  createThread: (agentId: string) => Promise<AgentThread>;
  selectThread: (thread: AgentThread | null) => void;
  fetchMessages: (threadId: string) => Promise<void>;
  sendMessage: (threadId: string, content: string) => Promise<string>;
  clearStreaming: () => void;

  fetchSchedules: (agentId: string) => Promise<void>;
  createSchedule: (agentId: string, data: Omit<AgentSchedule, 'id' | 'agent_id' | 'tenant_id' | 'last_run_at' | 'created_at' | 'updated_at'>) => Promise<AgentSchedule>;
  updateSchedule: (agentId: string, scheduleId: string, data: Partial<AgentSchedule>) => Promise<void>;
  deleteSchedule: (agentId: string, scheduleId: string) => Promise<void>;
  runScheduleNow: (agentId: string, scheduleId: string) => Promise<void>;

  fetchTriggers: (agentId?: string, pipelineId?: string) => Promise<void>;
  createTrigger: (data: PipelineTriggerCreate) => Promise<PipelineTrigger>;
  updateTrigger: (triggerId: string, data: Partial<PipelineTriggerCreate>) => Promise<void>;
  deleteTrigger: (triggerId: string) => Promise<void>;
  testFireTrigger: (triggerId: string, body: { new_row_ids?: string[]; all_row_ids?: string[]; object_type?: string }) => Promise<Record<string, unknown>>;

  versions: AgentVersion[];
  fetchVersions: (agentId: string) => Promise<void>;
  restoreVersion: (agentId: string, versionId: string) => Promise<void>;
}

export const useAgentStore = create<AgentStore>((set, get) => ({
  agents: [],
  selectedAgent: null,
  threads: [],
  selectedThread: null,
  messages: [],
  availableTools: [],
  loading: false,
  sending: false,
  streamingText: '',
  streamingTools: [],
  chatError: null,
  schedules: [],
  triggers: [],
  versions: [],

  fetchAgents: async () => {
    set({ loading: true });
    try {
      const r = await fetch(`${AGENT_API}/agents`, {
        headers: { 'x-tenant-id': getTenantId() },
      });
      const data = await r.json();
      set({ agents: Array.isArray(data) ? data : [] });
    } finally {
      set({ loading: false });
    }
  },

  selectAgent: (agent) => set({ selectedAgent: agent, threads: [], selectedThread: null, messages: [] }),

  createAgent: async (data) => {
    const r = await fetch(`${AGENT_API}/agents`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!r.ok) {
      const body = await r.json().catch(() => null);
      throw new Error(typeof body?.detail === 'string' ? body.detail : `HTTP ${r.status}`);
    }
    const agent = await r.json();
    set((s) => ({ agents: [agent, ...s.agents], selectedAgent: agent }));
    return agent;
  },

  updateAgent: async (id, data) => {
    const r = await fetch(`${AGENT_API}/agents/${id}`, {
      method: 'PUT',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const agent = await r.json();
    set((s) => ({
      agents: s.agents.map((a) => (a.id === id ? agent : a)),
      selectedAgent: s.selectedAgent?.id === id ? agent : s.selectedAgent,
    }));
  },

  deleteAgent: async (id) => {
    const r = await fetch(`${AGENT_API}/agents/${id}`, {
      method: 'DELETE',
      headers: { 'x-tenant-id': getTenantId() },
    });
    if (!r.ok) {
      // Previously the local-state removal happened regardless of status,
      // so a failed delete made the agent vanish then reappear on next
      // fetchAgents() — looked like "I can't delete it". Surface the
      // failure so the caller can show it and leave the row in place.
      let detail = `${r.status}`;
      try {
        const j = await r.json();
        if (j?.detail) detail = String(j.detail);
      } catch {
        const t = await r.text().catch(() => '');
        if (t) detail = t.slice(0, 200);
      }
      throw new Error(`Failed to delete agent (${detail})`);
    }
    set((s) => ({
      agents: s.agents.filter((a) => a.id !== id),
      selectedAgent: s.selectedAgent?.id === id ? null : s.selectedAgent,
    }));
  },

  setKnowledgeScope: async (id, scope) => {
    const r = await fetch(`${AGENT_API}/agents/${id}/knowledge-scope`, {
      method: 'PUT',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ scope }),
    });
    const agent = await r.json();
    set((s) => ({
      agents: s.agents.map((a) => (a.id === id ? agent : a)),
      selectedAgent: s.selectedAgent?.id === id ? agent : s.selectedAgent,
    }));
  },

  fetchAvailableTools: async () => {
    const r = await fetch(`${AGENT_API}/agents/tools`, {
      headers: { 'x-tenant-id': getTenantId() },
    });
    const data = await r.json();
    set({ availableTools: data.tools || [] });
  },

  fetchThreads: async (agentId) => {
    const r = await fetch(`${AGENT_API}/threads?agent_id=${agentId}`, {
      headers: { 'x-tenant-id': getTenantId() },
    });
    const data = await r.json();
    set({ threads: Array.isArray(data) ? data : [] });
  },

  createThread: async (agentId) => {
    const threadPrefix = i18n.t('agents.thread', { defaultValue: 'Hilo' });
    const r = await fetch(`${AGENT_API}/threads/${agentId}`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: `${threadPrefix} ${new Date().toLocaleTimeString()}` }),
    });
    const thread = await r.json();
    set((s) => ({
      threads: [thread, ...s.threads],
      selectedThread: thread,
      messages: [],
    }));
    return thread;
  },

  selectThread: (thread) => {
    set({ selectedThread: thread, messages: [], chatError: null });
    if (thread) get().fetchMessages(thread.id);
  },

  fetchMessages: async (threadId) => {
    const r = await fetch(`${AGENT_API}/threads/${threadId}/messages`, {
      headers: { 'x-tenant-id': getTenantId() },
    });
    const data = await r.json();
    set({ messages: Array.isArray(data) ? data : [] });
  },

  sendMessage: async (threadId, content) => {
    set({ sending: true, streamingText: '', streamingTools: [], chatError: null });
    const userMsg: AgentMessage = {
      id: `tmp-${Date.now()}`,
      thread_id: threadId,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };
    set((s) => ({ messages: [...s.messages, userMsg] }));

    let finalText = '';
    let sawTerminal = false;
    let streamError: Omit<ChatTurnError, 'threadId' | 'content'> | null = null;
    try {
      const response = await fetch(`${AGENT_API}/threads/${threadId}/messages`, {
        method: 'POST',
        headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, stream: true }),
      });

      if (!response.ok || !response.body) {
        streamError = { code: 'http_error', message: '', retryable: true };
      } else {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'text_delta') {
                finalText += event.text;
                set({ streamingText: finalText });
              } else if (event.type === 'tool_start') {
                set((s) => ({
                  streamingTools: [...s.streamingTools, { name: event.tool, status: 'calling' as const }],
                }));
              } else if (event.type === 'tool_result') {
                set((s) => ({
                  streamingTools: s.streamingTools.map((t) =>
                    t.name === event.tool && t.status === 'calling'
                      ? { ...t, status: 'done' as const, result: event.result }
                      : t
                  ),
                }));
              } else if (event.type === 'error') {
                sawTerminal = true;
                streamError = {
                  code: String(event.code || 'provider_error'),
                  message: String(event.message || event.error || ''),
                  retryable: event.retryable !== false,
                };
              } else if (event.type === 'done') {
                sawTerminal = true;
              }
            } catch { /* partial JSON */ }
          }
        }
      }
    } catch {
      streamError = { code: 'network', message: '', retryable: true };
    } finally {
      // A stream that closes with no terminal event and no answer is the
      // silent failure this guards against: show it instead of nothing.
      if (!streamError && !sawTerminal && !finalText) {
        streamError = { code: 'empty_stream', message: '', retryable: true };
      }
      set({ sending: false, streamingText: '', streamingTools: [] });
      // Small delay so the backend's finally-block DB save completes before we reload
      await new Promise((r) => setTimeout(r, 400));
      try { await get().fetchMessages(threadId); } catch { /* keep the optimistic list */ }
      if (streamError) {
        set({ chatError: { ...streamError, threadId, content } });
      }
    }
    return finalText;
  },

  clearStreaming: () => set({ streamingText: '', streamingTools: [] }),

  fetchSchedules: async (agentId) => {
    const r = await fetch(`${AGENT_API}/agents/${agentId}/schedules`, {
      headers: { 'x-tenant-id': getTenantId() },
    });
    const data = await r.json();
    set({ schedules: Array.isArray(data) ? data : [] });
  },

  createSchedule: async (agentId, data) => {
    const r = await fetch(`${AGENT_API}/agents/${agentId}/schedules`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const schedule = await r.json();
    set((s) => ({ schedules: [schedule, ...s.schedules] }));
    return schedule;
  },

  updateSchedule: async (agentId, scheduleId, data) => {
    const r = await fetch(`${AGENT_API}/agents/${agentId}/schedules/${scheduleId}`, {
      method: 'PUT',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const schedule = await r.json();
    set((s) => ({ schedules: s.schedules.map((sc) => (sc.id === scheduleId ? schedule : sc)) }));
  },

  deleteSchedule: async (agentId, scheduleId) => {
    await fetch(`${AGENT_API}/agents/${agentId}/schedules/${scheduleId}`, {
      method: 'DELETE',
      headers: { 'x-tenant-id': getTenantId() },
    });
    set((s) => ({ schedules: s.schedules.filter((sc) => sc.id !== scheduleId) }));
  },

  runScheduleNow: async (agentId, scheduleId) => {
    await fetch(`${AGENT_API}/agents/${agentId}/schedules/${scheduleId}/run-now`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId() },
    });
  },

  fetchTriggers: async (agentId, pipelineId) => {
    const params = new URLSearchParams();
    if (agentId) params.set('agent_id', agentId);
    if (pipelineId) params.set('pipeline_id', pipelineId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const r = await fetch(`${AGENT_API}/triggers${qs}`, {
      headers: { 'x-tenant-id': getTenantId() },
    });
    const data = await r.json();
    set({ triggers: Array.isArray(data) ? data : [] });
  },

  createTrigger: async (data) => {
    const r = await fetch(`${AGENT_API}/triggers`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!r.ok) throw new Error(`Create trigger failed: ${r.status} ${await r.text()}`);
    const trigger = await r.json();
    set((s) => ({ triggers: [trigger, ...s.triggers] }));
    return trigger;
  },

  updateTrigger: async (triggerId, data) => {
    const r = await fetch(`${AGENT_API}/triggers/${triggerId}`, {
      method: 'PUT',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!r.ok) throw new Error(`Update trigger failed: ${r.status} ${await r.text()}`);
    const trigger = await r.json();
    set((s) => ({ triggers: s.triggers.map((t) => (t.id === triggerId ? trigger : t)) }));
  },

  deleteTrigger: async (triggerId) => {
    await fetch(`${AGENT_API}/triggers/${triggerId}`, {
      method: 'DELETE',
      headers: { 'x-tenant-id': getTenantId() },
    });
    set((s) => ({ triggers: s.triggers.filter((t) => t.id !== triggerId) }));
  },

  testFireTrigger: async (triggerId, body) => {
    const r = await fetch(`${AGENT_API}/triggers/${triggerId}/test-fire`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error(`Test fire failed: ${r.status} ${await r.text()}`);
    return await r.json();
  },

  fetchVersions: async (agentId) => {
    const r = await fetch(`${AGENT_API}/agents/${agentId}/versions`, {
      headers: { 'x-tenant-id': getTenantId() },
    });
    const data = await r.json();
    set({ versions: Array.isArray(data) ? data : [] });
  },

  restoreVersion: async (agentId, versionId) => {
    const r = await fetch(`${AGENT_API}/agents/${agentId}/versions/${versionId}/restore`, {
      method: 'POST',
      headers: { 'x-tenant-id': getTenantId() },
    });
    const agent = await r.json();
    set((s) => ({
      agents: s.agents.map((a) => (a.id === agentId ? agent : a)),
      selectedAgent: s.selectedAgent?.id === agentId ? agent : s.selectedAgent,
    }));
  },
}));
